import { useRef } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { ImageIcon, Trash2 } from 'lucide-react';
import { useEventImageSrc } from '@/hooks/useEventImageSrc';

interface ImagePickerProps {
  image: File | null;
  imagePreview?: string | null;
  onImageChange: (file: File | null) => void;
  onRemoveImage?: () => void;
  disabled?: boolean;
}

export function ImagePicker({
  image,
  imagePreview,
  onImageChange,
  onRemoveImage,
  disabled = false,
}: ImagePickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Resolve the stored DB URL to a signed URL for the private bucket.
  // When a new local File is selected, we bypass this with URL.createObjectURL below.
  const signedPreview = useEventImageSrc(image ? null : imagePreview);

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageChange(file);
    } else {
      onImageChange(null);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    if (onRemoveImage) {
      onRemoveImage();
    } else {
      onImageChange(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayImage = image ? URL.createObjectURL(image) : signedPreview;

  return (
    <Box mb={{ base: '4', md: '0' }}>
      <chakra.label
        htmlFor="event-image-input"
        display="block"
        mb="2"
        fontSize={{ base: 'sm', md: 'md' }}
        fontWeight="medium"
        color="volyx.text"
      >
        Event Photo
      </chakra.label>
      <Box
        position="relative"
        h="40"
        borderRadius="xl"
        bg="volyx.lightGray"
        overflow="hidden"
        w="full"
        boxShadow="0 1px 3px rgba(0,0,0,0.08)"
        transition="all 0.2s"
        _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
      >
        <chakra.button
          id="event-image-button"
          w="full"
          h="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          border="none"
          bg="transparent"
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          transition="transform 0.3s"
          _hover={{ transform: 'scale(1.02)' }}
          onClick={handlePickImage}
          disabled={disabled}
        >
          {displayImage ? (
            <chakra.img
              src={displayImage}
              alt="Preview"
              w="full"
              h="full"
              objectFit="cover"
            />
          ) : (
            <Text as="span" color="volyx.textSecondary" fontWeight="medium">Upload Image</Text>
          )}
        </chakra.button>
        {displayImage && (
          <Flex position="absolute" bottom="3" right="3" gap="2">
            <chakra.button
              onClick={handlePickImage}
              disabled={disabled}
              w="10"
              h="10"
              bg="white"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 4px 12px rgba(0,0,0,0.08)"
              _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'scale(1.1)' }}
              _active={{ transform: 'scale(0.95)' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              transition="all 0.3s"
              border="0"
              outline="none"
              _focus={{ outline: 'none' }}
              cursor="pointer"
              aria-label="Change image"
            >
              <Box as={ImageIcon} boxSize="22px" color="volyx.action" />
            </chakra.button>
            <chakra.button
              onClick={handleRemoveImage}
              disabled={disabled}
              w="10"
              h="10"
              bg="white"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 4px 12px rgba(0,0,0,0.08)"
              _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'scale(1.1)' }}
              _active={{ transform: 'scale(0.95)' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              transition="all 0.3s"
              border="0"
              outline="none"
              _focus={{ outline: 'none' }}
              cursor="pointer"
              aria-label="Remove image"
            >
              <Box as={Trash2} boxSize="20px" color="volyx.error" />
            </chakra.button>
          </Flex>
        )}
      </Box>
      <chakra.input
        id="event-image-input"
        name="event-image"
        ref={fileInputRef}
        type="file"
        accept="image/*"
        display="none"
        onChange={handleFileChange}
        disabled={disabled}
      />
    </Box>
  );
}
